import { useEffect, useState, useRef, MutableRefObject } from 'react';
import { createPortal } from 'react-dom';
import { useClickAway } from 'react-use';
import { useEvent, useUserBusinessCardAtom } from '@bizzabo/entities';
import { sendCustomClick } from '@bizzabo/widget-analytics';
import { useLocation } from 'react-router-dom';

import UserInfo from '../UserInfo';
import useUserBusinessCard from './useUserBusinessCard';

type Position = {
    x: number | null;
    y: number | null;
};

const initialPosition: Position = {
    x: null,
    y: null,
};

const offsetX = 41; // distance in px between the preview card left border and the center of the avatar
const offsetY = 41; // distance in px between the preview card top border and the center of the avatar
const cardIndent = 30;

function useClickOutside(
    userPopupRef: MutableRefObject<HTMLDivElement>,
    closeUserBusinessCard: () => void
) {
    const location = useLocation();

    useEffect(() => {
        closeUserBusinessCard();
    }, [location]);

    useClickAway(userPopupRef, closeUserBusinessCard);
}

const UserBusinessCard: React.FC<{ eventUniqueName: string }> = ({ eventUniqueName }) => {
    const [position, setPosition] = useState(initialPosition);
    const { data: event } = useEvent(eventUniqueName);

    const {
        closeUserPopup: closeUserBusinessCard,
        showId,
        userPopupSource: source,
        avatarRef,
        isUserModerator,
    } = useUserBusinessCardAtom();

    const userPopupRef = useRef<HTMLDivElement>(null);

    useClickOutside(userPopupRef, closeUserBusinessCard);

    const scrollEventListener = () => {
        if (showId && avatarRef?.['current']) {
            closeUserBusinessCard();
            setPosition(initialPosition);
        }
    };

    useEffect(() => {
        document.addEventListener('scroll', scrollEventListener, true);
        return () => {
            document.removeEventListener('scroll', scrollEventListener, true);
        };
    }, [showId]);

    const { user, isLoading } = useUserBusinessCard(event?.id, showId, source);

    useEffect(() => {
        calculatePopUpPosition();
    }, [showId, avatarRef, userPopupRef.current, isLoading]);

    /**
     * This function calculates UserBusinessCard position on the screen in two steps:
     * 1. Basic calculation depending on UserAvatar position.
     * 2. When we loaded the user data and calculated step 1, and UserBusinessCard is outside of the screen we calculate y once more.
     */
    const calculatePopUpPosition = () => {
        if (showId && avatarRef?.['current']) {
            const avatar = avatarRef?.['current']?.['getBoundingClientRect']();
            if (avatar) {
                let x = avatar.x + avatar.width / 2 - offsetX + window.scrollX;
                let y = avatar.y + avatar.height / 2 - offsetY + window.scrollY;

                if (x < 0) {
                    x = 0;
                }

                if (!isLoading && y === position.y) {
                    const popup = userPopupRef.current?.['getBoundingClientRect']();
                    if (popup?.height && popup.height + y > window.innerHeight) {
                        y = window.innerHeight - popup.height - cardIndent;
                    }
                }

                setPosition({ x, y });
            }
        } else {
            setPosition(initialPosition);
        }
    };

    const contactAttendee = () => {
        closeUserBusinessCard();
        sendCustomClick(
            {
                buttonType: 'SESSION_ENGAGEMENT_PANEL',
                subCategory: 'DIRECT_MESSAGING_CHAT',
                description: JSON.stringify({
                    attendee_id: user.userProfileId,
                    match_type: 'session',
                    details_provided: {
                        firstName: user.firstName,
                        lastName: user.lastName,
                        title: user.title,
                        company: user.company,
                        summary: user.summary,
                        linkedinId: user.linkedinId,
                        twitterHandle: user.twitterHandle,
                        photoUrl: user.photoUrl,
                    },
                }),
            },
            true
        );
    };

    return createPortal(
        <UserInfo
            user={user}
            ref={userPopupRef}
            closePopup={closeUserBusinessCard}
            position={position}
            onButtonClick={contactAttendee}
            isLoading={isLoading}
            isUserModerator={isUserModerator}
        />,
        document.body
    );
};

export default UserBusinessCard;
